import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

interface SectionCardProps {
    title: string
    description?: ReactNode
    action?: ReactNode
    footer?: ReactNode
    children: ReactNode
    id?: string
    className?: string
    contentClassName?: string
}

// The standard dashboard panel: a titled card with an optional action and footer
export const SectionCard: FC<SectionCardProps> = ({
    title,
    description,
    action,
    footer,
    children,
    id,
    className,
    contentClassName
}): ReactNode => {
    const headingId = useId()

    return (
        <section
            id={id}
            aria-labelledby={headingId}
            className={cn(
                'flex min-w-0 scroll-mt-24 flex-col rounded-xl border bg-card text-card-foreground shadow-[0_1px_2px_0_rgb(28_25_23/0.04)]',
                className
            )}
        >
            <header className='flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-5 pt-5'>
                <div className='min-w-0 flex-1'>
                    <h2 id={headingId} className='font-heading text-[15px] leading-snug font-semibold text-foreground'>
                        {title}
                    </h2>

                    {description && (
                        <p className='mt-1 text-[13px] leading-relaxed text-muted-foreground'>
                            {description}
                        </p>
                    )}
                </div>

                {action && (
                    <div className='flex shrink-0 items-center gap-2'>
                        {action}
                    </div>
                )}
            </header>

            <div className={cn('flex-1 px-5 pt-4 pb-5', contentClassName)}>
                {children}
            </div>

            {footer && (
                <div className='border-t px-5 py-3'>
                    {footer}
                </div>
            )}
        </section>
    )
}
